import { useCallback, useEffect, useState } from 'react';

const SPLIT_RATIO_KEY = 'omnis:panel-split-ratio';

const SPLIT_MIN_RATIO = 0.3;
const SPLIT_MAX_RATIO = 0.75;

const loadNumber = (key, fallback) => {
    try {
        const raw = localStorage.getItem(key);
        if (raw == null) return fallback;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : fallback;
    } catch (_) {
        return fallback;
    }
};

/**
 * Persistent split-view ratio. The active panel's share of the viewport
 * (panel-width / available-width). Defaults to 0.55 — a slightly wider
 * panel than chat to give the feature some room to breathe.
 *
 * Persists to localStorage so the user's preferred layout follows them.
 */
export function usePanelSize() {
    const [splitRatio, setSplitRatioState] = useState(() => loadNumber(SPLIT_RATIO_KEY, 0.55));

    // Re-clamp on viewport changes — a previously-saved ratio could become
    // unreasonable if the user moves to a much narrower screen.
    useEffect(() => {
        const handleResize = () => {
            setSplitRatioState((prev) => Math.max(SPLIT_MIN_RATIO, Math.min(SPLIT_MAX_RATIO, prev)));
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const setSplitRatio = useCallback((ratio) => {
        const clamped = Math.max(SPLIT_MIN_RATIO, Math.min(SPLIT_MAX_RATIO, ratio));
        setSplitRatioState(clamped);
        try {
            localStorage.setItem(SPLIT_RATIO_KEY, String(clamped));
        } catch (_) { /* ignore */ }
    }, []);

    return {
        splitRatio,
        setSplitRatio,
        splitMinRatio: SPLIT_MIN_RATIO,
        splitMaxRatio: SPLIT_MAX_RATIO,
    };
}
